import { PostSetStatus, SetType } from '../enums';
import { clamp } from '../shared-util';
import { Log_WorkoutSet, removeMatchingPostStatuses } from './log-workout';

export interface VolumeAutoregulationInputs {
  /** 0-based index of the just-completed set within the exercise. */
  setIndex: number;
  /** Set type of the just-completed set. Only Followup sets trigger evaluation. */
  setType: SetType;
  /** Total count of top-level sets in the exercise (NOT mini-sets). */
  numSets: number;
  /** Baseline top-set count for this subslot. For myo-rep exercises this MUST
   *  be the post-translation count (e.g. via getMyoAwareNumSets), NOT the raw
   *  planSubSlot.defaultVolume_sets, which for myo-reps is a combined
   *  classic-equivalent volume. */
  baselineNumSets: number;
  /** Current stored volumeAdjustment on planPath, or undefined if not set. */
  volumeAdjustment: -1 | 0 | 1 | undefined;
  firstSetReps: number;
  firstSetWeightKg: number;
  firstSetPlannedWeightKg: number;
  currentSetReps: number;
  currentSetWeightKg: number;
  /** true when the user has already given a decisive answer — accepted or
   *  declined — to a volume suggestion for this exercise in this workout.
   *  The caller works this out; the rule below simply stops. (#4714) */
  alreadyAnsweredThisWorkout?: boolean;
}

export interface VolumeAutoregulationDecision {
  direction: 'increase' | 'decrease';
  newVolumeAdjustment: -1 | 0 | 1;
  currentVolumeAdjustment: -1 | 0 | 1;
}

/** Pure decision: given the just-completed set and exercise context, returns
 *  a recommendation to change `volumeAdjustment` for the next workout, or
 *  null if no change is warranted. Side effects (persisting, dialog) happen
 *  at the call site. */
export function computeVolumeAutoregulation(
  input: VolumeAutoregulationInputs,
): VolumeAutoregulationDecision | null {
  // only evaluate on followup sets after the first set
  if (input.setIndex < 1 || input.setType !== SetType.Followup) return null;

  // if the user has already answered for this exercise in this workout, say nothing
  // more about it today. answering deletes the plan-side fields that would otherwise
  // stop a second card, so without this the next set asks again — and after an accept
  // it can ask for the opposite change, because accepting moved volumeAdjustment.
  // nothing below this line changed: which set is judged, and every threshold, is
  // exactly as it was. see design-docs/volume-autoregulation.md (#4714)
  if (input.alreadyAnsweredThisWorkout) return null;

  // preserve legacy "0 is falsy → infer from set count" behaviour
  const currentVolumeAdjustment = (input.volumeAdjustment
    || clamp(input.numSets - input.baselineNumSets, -1, 1)) as -1 | 0 | 1;
  let newVolumeAdjustment: -1 | 0 | 1 = currentVolumeAdjustment;

  // downregulation: reps drop to <50% of first set at non-higher weight
  if (input.currentSetReps < input.firstSetReps * 0.5
      && input.currentSetWeightKg <= input.firstSetWeightKg + 0.01) {
    const proposedVolume = input.numSets - 1;
    if (input.numSets > input.baselineNumSets) {
      newVolumeAdjustment = 0;
    } else if (proposedVolume >= 2) {
      newVolumeAdjustment = -1;
    }
  }

  // upregulation check only fires on the "last" set
  // (or second-to-last if we're already upregulated past baseline)
  const upregCheckIndex = input.numSets - Math.max(input.numSets - input.baselineNumSets, 0) - 1;
  if (input.setIndex === upregCheckIndex) {
    const perfGood = input.currentSetReps > input.firstSetReps * 0.8
      && input.currentSetWeightKg >= input.firstSetWeightKg
      && input.firstSetWeightKg >= input.firstSetPlannedWeightKg;

    if (perfGood) {
      newVolumeAdjustment = input.numSets < input.baselineNumSets ? 0 : 1;
    } else if (input.numSets < input.baselineNumSets) {
      const proposedVolume = input.numSets - 1;
      if (proposedVolume >= 2) {
        newVolumeAdjustment = -1;
      }
    }
  }

  if (newVolumeAdjustment === currentVolumeAdjustment) return null;

  return {
    direction: newVolumeAdjustment > currentVolumeAdjustment ? 'increase' : 'decrease',
    newVolumeAdjustment,
    currentVolumeAdjustment,
  };
}

/**
 * Works out the `volumeAdjustment` to store when the user ACCEPTS a pending
 * volume suggestion.
 *
 * The answer is usually the value the rule decided, which the accept path reads
 * back out of the plan path where the suggestion was raised
 * (`autoregulationNewVolumeAdjustment`). It is a function rather than a plain
 * field read because of the two cases where that value cannot be used as it
 * stands, and both fall back to the same thing: step the stored adjustment one
 * up or down, in the direction the user was shown.
 *
 * **Case one — the suggestion predates the stored value.** A suggestion raised
 * by a build from before `autoregulationNewVolumeAdjustment` existed carries
 * only its direction, and the step is the best that can be done for it.
 *
 * **Case two — the baseline moved before the user answered.** The decided value
 * is an ABSOLUTE position relative to the baseline set count the rule measured,
 * not a step: accepting is meant to make the next workout hold
 * `baseline + decided` sets. But the dialog offers the user a step ("we
 * recommend increasing your set volume by 1 set"), and the baseline can move
 * between the offer and the answer — an explicit set-count write from the
 * exercise Settings page or from Volume Optimization (`setPlanSetCount`, which
 * also zeroes `volumeAdjustment` on every path), or a program rebuild, which
 * writes a fresh base while `carrySubSlotStateAcrossRebuild` carries the paths
 * over. Applied to a base that has moved, the decided value keeps its old
 * arithmetic and loses its meaning: where it is 0 and the path now sits at 0,
 * accepting writes the value the exercise already had, so the card goes away
 * and the set count never moves — a decisive answer that silently does nothing.
 * Stepping from where the exercise now is delivers what the user was told.
 *
 * A step cannot leave the -1..1 range the adjustment is capped to, so stepping
 * from a carried 1 towards a further increase stays at 1. That cap is inherent
 * to the adjustment and is not something this fallback introduces.
 *
 * **The moved-baseline step carries the rule's own floor with it.** A step DOWN
 * that would leave fewer than 2 sets keeps the adjustment the path already
 * carries, because `computeVolumeAutoregulation` never proposes such a decrease
 * itself. Only a step down is held back, and case one never reaches that floor
 * — both are load-bearing rather than tidy, and
 * `design-docs/volume-autoregulation.md`, "The baseline can move while a
 * suggestion is unanswered", says why.
 *
 * **Every answer keeps at least one set (#6665).** Whichever road it took, an
 * answer that would leave today's baseline with no sets is raised just enough to
 * leave one. That covers case one, and a decided -1 accepted after the baseline
 * dropped when no baseline was recorded to notice. It only ever raises an answer,
 * so a step up is never blocked.
 *
 * Why the old step is not good enough in general, and so is only a fallback:
 * the rule compares against a CURRENT adjustment it infers from the set count
 * when nothing is stored (`volumeAdjustment || clamp(numSets - baseline)`),
 * while the step starts from the stored value alone. When those two differ the
 * step lands somewhere the rule never chose. It also cannot express a two-step
 * move at all — the rule really does answer -1 against a current of 1 — and a
 * step from 1 stops at 0. The value it used to land on could be one lower than
 * the rule asked for, and an exercise whose planned volume is 1 then gets
 * planned with no sets at all. See `design-docs/volume-autoregulation.md`.
 *
 * @param decidedNewVolumeAdjustment what the rule decided when it raised the
 *        suggestion, or undefined for a suggestion raised before that was stored
 * @param storedVolumeAdjustment the adjustment on the path right now
 * @param direction the suggestion's direction, used only by the fallback
 * @param baselineSetsWhenRaised the baseline set count the rule measured against
 *        when it raised the suggestion, or undefined for a suggestion raised
 *        before that was stored
 * @param baselineSetsNow the baseline set count of the sub-slot the answer is
 *        about to be written into. Pass undefined when it cannot be read; the
 *        staleness test then does not fire, which leaves the decided value in
 *        charge exactly as before.
 */
export function resolveAcceptedVolumeAdjustment(
  decidedNewVolumeAdjustment: -1 | 0 | 1 | undefined,
  storedVolumeAdjustment: -1 | 0 | 1 | undefined,
  direction: 'increase' | 'decrease' | undefined,
  baselineSetsWhenRaised?: number,
  baselineSetsNow?: number,
): -1 | 0 | 1 {
  // the decided value is a position measured against the baseline that was
  // there when the suggestion was raised, so it only means what it says while
  // that baseline is still the one being written to. both numbers have to be
  // known for the comparison to say anything.
  const baselineMoved = baselineSetsWhenRaised !== undefined
    && baselineSetsNow !== undefined
    && baselineSetsWhenRaised !== baselineSetsNow;

  // if the rule's own answer travelled with the suggestion and still applies,
  // that is the answer
  if (decidedNewVolumeAdjustment !== undefined && !baselineMoved) {
    return atLeastOneSet(decidedNewVolumeAdjustment, baselineSetsNow);
  }
  // otherwise step from where the exercise is now, in the direction the user was
  // shown — for a suggestion raised by an older build, and for one whose
  // baseline moved before it was answered
  const current = (storedVolumeAdjustment || 0) as -1 | 0 | 1;
  const stepped = clamp(current + (direction === 'increase' ? 1 : -1), -1, 1) as -1 | 0 | 1;

  // if a moved baseline has left the exercise too small to take the step DOWN,
  // keep what it has — the same 2-set floor `computeVolumeAutoregulation` applies
  // to its own decreases. Held back for a step down only; why a step up must NOT
  // be, and why a pre-field suggestion never reaches this, are in
  // design-docs/volume-autoregulation.md, "The baseline can move while a
  // suggestion is unanswered".
  if (baselineMoved && stepped < current && baselineSetsNow + stepped < 2) {
    return current;
  }

  // a pre-field step is held only by the 1-set floor every answer gets (#6665)
  return atLeastOneSet(stepped, baselineSetsNow);
}

/** Raise an answer just enough that it leaves at least one set on today's base (#6665).
 *  It only ever raises, so it never blocks a step up; with today's base unknown it
 *  changes nothing, and so does a damaged one (null, NaN), which would otherwise
 *  turn a decrease into an increase. */
function atLeastOneSet(answer: -1 | 0 | 1, baselineSetsNow: number | undefined): -1 | 0 | 1 {
  if (typeof baselineSetsNow !== 'number' || !Number.isFinite(baselineSetsNow)) {
    return answer;
  }
  return Math.max(answer, 1 - baselineSetsNow) as -1 | 0 | 1;
}

/** Removes every VolumeAutoregulationPending post-set status from the given
 *  sets. This status is what renders the "Resolve" card on the workout-detail
 *  page, so resolving a suggestion must sweep ALL sets — not just the set the
 *  stored trigger index points at. The stored index can drift away from the
 *  set that actually carries the status (a re-trigger overwrites the index
 *  without moving the status, see #4052), and a single-set cleanup then
 *  leaves a stuck card whose Resolve button silently does nothing.
 *  Returns the number of statuses removed (0 means there was nothing to
 *  clean). */
export function clearPendingVolumeAutoregulationStatuses(sets: Log_WorkoutSet[]): number {
  return removeMatchingPostStatuses(sets, status => status === PostSetStatus.VolumeAutoregulationPending);
}
